import { useState } from "react";
import { Button } from "@/components/ui/button";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { useCart, formatUSD } from "@/contexts/CartContext";
import { Link } from "react-router-dom";
import { Lock, ShoppingBag, Trash2, ArrowLeft } from "lucide-react";
import { StripeEmbeddedCheckout } from "@/components/StripeEmbeddedCheckout";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";

const Checkout = () => {
  const { items, subtotal, removeItem, updateQuantity } = useCart();
  const [payOpen, setPayOpen] = useState(false);

  const returnUrl = `${window.location.origin}/checkout/return?session_id={CHECKOUT_SESSION_ID}`;

  return (
    <div className="min-h-screen">
      <Navigation />
      <PaymentTestModeBanner />

      <main className="container mx-auto px-4 pt-32 md:pt-40 pb-16">
        <h1 className="font-orbitron font-bold text-3xl md:text-4xl mb-2">
          Secure <span className="text-primary">Checkout</span>
        </h1>
        <p className="text-muted-foreground mb-8 flex items-center gap-2 text-sm">
          <Lock className="h-4 w-4" /> Encrypted payment processing by Stripe
        </p>

        {items.length === 0 ? (
          <Card className="p-10 text-center glass-effect border-[hsl(var(--line))]">
            <ShoppingBag className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
            <p className="mb-4 text-muted-foreground">Your cart is empty.</p>
            <Button asChild>
              <Link to="/accessories">Browse Accessories</Link>
            </Button>
          </Card>
        ) : payOpen ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Button
                variant="ghost"
                size="sm"
                className="mb-3"
                onClick={() => setPayOpen(false)}
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Back to cart
              </Button>
              <Card className="p-2 sm:p-4 glass-effect border-[hsl(var(--line))]">
                <StripeEmbeddedCheckout returnUrl={returnUrl} />
              </Card>
            </div>
            <Card className="p-6 h-fit glass-effect border-[hsl(var(--line))] sticky top-32">
              <h2 className="font-orbitron font-bold text-xl mb-4">Order Summary</h2>
              <ul className="space-y-2 text-sm mb-4">
                {items.map((i) => (
                  <li key={i.id} className="flex justify-between gap-2">
                    <span className="text-muted-foreground truncate">
                      {i.name} × {i.quantity}
                    </span>
                    <span>{formatUSD(i.price * i.quantity)}</span>
                  </li>
                ))}
              </ul>
              <div className="border-t border-[hsl(var(--line))] pt-3 flex justify-between font-bold">
                <span>Subtotal</span>
                <span className="text-primary">{formatUSD(subtotal)}</span>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">
                Shipping & tax calculated at payment.
              </p>
            </Card>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              {items.map((i) => (
                <Card
                  key={i.id}
                  className="p-4 flex gap-4 glass-effect border-[hsl(var(--line))]"
                >
                  {i.image && (
                    <img
                      src={i.image}
                      alt={i.name}
                      className="w-20 h-20 object-contain bg-white/5 rounded p-2 shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold">{i.name}</p>
                    {i.category && (
                      <p className="text-[11px] text-primary uppercase tracking-wider mt-0.5">
                        {i.category}
                      </p>
                    )}
                    <div className="flex items-center gap-3 mt-2">
                      <label className="text-xs text-muted-foreground">Qty</label>
                      <input
                        type="number"
                        min={1}
                        value={i.quantity}
                        onChange={(e) =>
                          updateQuantity(i.id, Math.max(1, parseInt(e.target.value || "1", 10)))
                        }
                        className="w-16 h-8 rounded border border-[hsl(var(--line))] bg-background/60 px-2 text-sm"
                      />
                      <button
                        onClick={() => removeItem(i.id)}
                        className="text-muted-foreground hover:text-destructive ml-auto"
                        aria-label="Remove"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-primary">{formatUSD(i.price * i.quantity)}</p>
                    <p className="text-xs text-muted-foreground">{formatUSD(i.price)} ea.</p>
                  </div>
                </Card>
              ))}
            </div>

            <Card className="p-6 h-fit glass-effect border-[hsl(var(--line))] sticky top-32">
              <h2 className="font-orbitron font-bold text-xl mb-4">Order Summary</h2>
              <div className="space-y-2 text-sm mb-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{formatUSD(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Shipping</span>
                  <span className="text-muted-foreground">Calculated at payment</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tax</span>
                  <span className="text-muted-foreground">Calculated at payment</span>
                </div>
              </div>
              <div className="border-t border-[hsl(var(--line))] pt-3 flex justify-between font-bold text-lg">
                <span>Total</span>
                <span className="text-primary">{formatUSD(subtotal)}</span>
              </div>
              <Button
                className="w-full mt-6 font-bold"
                size="lg"
                onClick={() => setPayOpen(true)}
              >
                Proceed to Payment
              </Button>
              <p className="text-[11px] text-muted-foreground mt-3 text-center">
                Powered by Stripe. Your payment info is encrypted.
              </p>
            </Card>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default Checkout;
